import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COLLECTIONS,
  getCollection,
  type CollectionGroup,
  type InventoryImportResult,
  type OrgRecord,
  type Paginated,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatBytes,
  formatDate,
  useToast,
} from '../../components/ui.js';
import { RecordWizard } from './RecordWizard.js';
import './records.css';
import '../import/import.css';

const ACCEPT = '.xlsx,.csv,.txt,.md,.docx,.pdf';

const COPY: Record<
  CollectionGroup,
  {
    title: string;
    subtitle: string;
    base: string;
    uploadTitle: string;
    uploadBlurb: string;
    emptyMessage: string;
    /** Empty state when the organisation has AI import turned off. */
    emptyMessageAiOff: string;
  }
> = {
  inventory: {
    title: 'All Assets',
    subtitle:
      'Everything in your inventory — snapshots of these lists can be imported into assessment sections as evidence',
    base: '/assets',
    uploadTitle: 'Import your inventory',
    uploadBlurb:
      'Upload an existing asset list — a spreadsheet export, CSV or document — and AI files each entry as draft software, hardware, suppliers or websites, enriching detail (make, model, OS, security posture) where it can. Drafts stay out of your live inventory until you approve them.',
    emptyMessage:
      'Add products, software, hardware, suppliers and websites from the Inventory menu — or import an existing list above.',
    emptyMessageAiOff:
      'Add products, software, hardware, suppliers and websites from the Inventory menu.',
  },
  registry: {
    title: 'All Registers',
    subtitle:
      'Every compliance register in one place — snapshots of these lists can be imported into assessment sections as evidence',
    base: '/registers',
    uploadTitle: 'Import your registers',
    uploadBlurb:
      'Upload an existing register — a risk log, incident spreadsheet, DPIA export or processing record — and AI files each entry as a draft in the right register, adding standards-informed classification (category, likelihood, impact) where it can. Drafts stay out of your live registers until you approve them.',
    emptyMessage:
      'Record risks, incidents, DPIAs and more from the Registries menu — or import an existing register above.',
    emptyMessageAiOff: 'Record risks, incidents, DPIAs and more from the Registries menu.',
  },
};

/** Roll-up page for one collection group: /assets (inventory) and /registers (registry). */
export function RecordsOverviewPage({ group }: { group: CollectionGroup }) {
  const { org } = useOrg();
  const copy = COPY[group];
  const collections = COLLECTIONS.filter((d) => d.group === group);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canWrite = org.role === 'admin' || org.role === 'contributor';
  // Organisation AI switch: the import dropzone and the draft-review panel
  // are AI import features, hidden when the organisation has AI turned off.
  const aiEnabled = org.aiEnabled !== false;

  const fileInput = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [lastResult, setLastResult] = useState<InventoryImportResult | null>(null);
  const [editing, setEditing] = useState<OrgRecord | null>(null);

  const records = useQuery({
    queryKey: ['records', org.orgId, `group:${group}`],
    queryFn: () =>
      apiFetch<Paginated<OrgRecord>>(`/orgs/${org.orgId}/records`, {
        query: { group, limit: 200 },
      }),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['records', org.orgId] });
  const onError = (err: unknown) => {
    if (err instanceof ApiRequestError && err.code === 'not_configured') {
      toast(
        'AI import is not configured on this environment — an OPENAI_API_KEY is required before inventories can be imported.',
        'error',
      );
      return;
    }
    toast(err instanceof ApiRequestError ? err.message : 'Something went wrong', 'error');
  };

  const upload = useMutation({
    mutationFn: (toUpload: File[]) => {
      const formData = new FormData();
      for (const file of toUpload) formData.append('file', file);
      return apiFetch<InventoryImportResult>(`/orgs/${org.orgId}/records/import`, {
        method: 'POST',
        query: { group },
        formData,
      });
    },
    onSuccess: (result) => {
      setLastResult(result);
      setFiles([]);
      toast(
        result.createdTotal > 0
          ? `${result.createdTotal} draft record${result.createdTotal === 1 ? '' : 's'} created — review them below.`
          : 'No new inventory items were found in the uploaded files.',
        result.createdTotal > 0 ? 'success' : 'default',
      );
      invalidate();
    },
    onError,
  });

  const patchRecord = useMutation({
    mutationFn: ({
      record,
      body,
    }: {
      record: OrgRecord;
      body: { status?: 'active' | 'archived' } | { collection: string };
      message: string;
    }) =>
      apiFetch<OrgRecord>(`/orgs/${org.orgId}/records/${record.id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (_, vars) => {
      toast(vars.message, 'success');
      invalidate();
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: (record: OrgRecord) =>
      apiFetch<{ ok: true }>(`/orgs/${org.orgId}/records/${record.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Draft deleted.', 'success');
      invalidate();
    },
    onError,
  });

  const items = records.data?.items ?? [];
  const active = useMemo(() => items.filter((r) => r.status === 'active'), [items]);
  const drafts = useMemo(
    () => (aiEnabled ? items.filter((r) => r.status === 'draft') : []),
    [items, aiEnabled],
  );

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of active) map.set(r.collection, (map.get(r.collection) ?? 0) + 1);
    return map;
  }, [active]);

  if (records.isLoading) return <LoadingPane label="Loading assets" />;

  const nameByKey = new Map(collections.map((d) => [d.key, d.name]));

  const addFiles = (incoming: FileList | null) => {
    if (upload.isPending) return;
    if (!incoming || incoming.length === 0) return;
    setFiles((current) => {
      const merged = [...current];
      for (const file of Array.from(incoming)) {
        if (!merged.some((f) => f.name === file.name && f.size === file.size)) merged.push(file);
      }
      return merged;
    });
  };

  return (
    <div>
      <PageHeader title={copy.title} subtitle={copy.subtitle} />

      <div className="collection-cards">
        {collections.map((d) => (
          <Card key={d.key}>
            <div className="collection-count">{counts.get(d.key) ?? 0}</div>
            <Link to={`${copy.base}/${d.key}`}>{d.name}</Link>
          </Card>
        ))}
      </div>

      {canWrite && !aiEnabled && (
        <Card title={copy.uploadTitle}>
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>AI import is turned off in Organisation settings</p>
        </Card>
      )}

      {canWrite && aiEnabled && (
        <Card title={copy.uploadTitle}>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>{copy.uploadBlurb}</p>
          <div
            className={`import-dropzone ${dragOver ? 'drag-over' : ''} ${upload.isPending ? 'disabled' : ''}`}
            aria-disabled={upload.isPending}
            onDragOver={(e) => {
              e.preventDefault();
              if (!upload.isPending) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              addFiles(e.dataTransfer.files);
            }}
          >
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={ACCEPT}
              className="sr-only"
              aria-label="Choose inventory files to import"
              disabled={upload.isPending}
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <p className="import-dropzone-hint">
              Drag and drop files here, or{' '}
              <Button
                variant="secondary"
                size="sm"
                disabled={upload.isPending}
                onClick={() => fileInput.current?.click()}
              >
                Choose files
              </Button>
            </p>
            <p className="import-dropzone-formats">Excel, CSV, text, Markdown, Word or PDF</p>
          </div>
          {files.length > 0 && (
            <ul className="import-file-list">
              {files.map((file) => (
                <li key={`${file.name}-${file.size}`}>
                  <span className="import-file-name">{file.name}</span>
                  <span className="import-file-size">{formatBytes(file.size)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${file.name}`}
                    disabled={upload.isPending}
                    onClick={() => setFiles((current) => current.filter((f) => f !== file))}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
            <Button
              loading={upload.isPending}
              disabled={files.length === 0}
              onClick={() => upload.mutate(files)}
            >
              Import {files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'}` : ''}
            </Button>
            {upload.isPending && (
              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
                Extracting and classifying entries — this can take a minute for larger files.
              </span>
            )}
          </div>

          {lastResult && (
            <div className="import-result" role="status">
              {lastResult.files.map((f) => (
                <p key={f.fileName} style={{ margin: '8px 0 0', fontSize: 'var(--text-sm)' }}>
                  <strong>{f.fileName}</strong>
                  {f.status === 'succeeded'
                    ? ` — ${f.created.length} draft${f.created.length === 1 ? '' : 's'} created` +
                      (f.skipped.length > 0
                        ? `, ${f.skipped.length} skipped (${[...new Set(f.skipped.map((s) => s.reason))].join('; ')})`
                        : '')
                    : ` — ${f.error ?? 'not processed'}`}
                </p>
              ))}
            </div>
          )}
        </Card>
      )}

      {drafts.length > 0 && (
        <Card title={`Draft imports (${drafts.length}) — review needed`}>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 0 }}>
            Imported items wait here until approved. Check the detail — AI fills in what it can and
            makes standards-informed assumptions — then approve, or move anything filed in the
            wrong place.
          </p>
          <Table
            head={
              <>
                <th>Name</th>
                <th>Collection</th>
                <th>Imported</th>
                {canWrite && <th />}
              </>
            }
          >
            {drafts.map((r) => (
              <tr key={r.id}>
                <td style={{ fontWeight: 600 }}>{r.name}</td>
                <td>
                  <Badge tone="warning">{nameByKey.get(r.collection) ?? r.collection}</Badge>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.createdAt)}</td>
                {canWrite && (
                  <td>
                    <div className="record-row-actions">
                      <Button
                        size="sm"
                        onClick={() =>
                          patchRecord.mutate({
                            record: r,
                            body: { status: 'active' },
                            message: `“${r.name}” approved into the inventory.`,
                          })
                        }
                      >
                        Approve
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setEditing(r)}>
                        Edit
                      </Button>
                      <Select
                        aria-label={`Move ${r.name} to another inventory`}
                        value=""
                        style={{ width: 'auto' }}
                        onChange={(e) => {
                          if (e.target.value) {
                            patchRecord.mutate({
                              record: r,
                              body: { collection: e.target.value },
                              message: `“${r.name}” moved to ${nameByKey.get(e.target.value) ?? e.target.value}.`,
                            });
                          }
                        }}
                      >
                        <option value="">Move to…</option>
                        {collections.filter((d) => d.key !== r.collection).map((d) => (
                          <option key={d.key} value={d.key}>
                            {d.name}
                          </option>
                        ))}
                      </Select>
                      <Button variant="danger" size="sm" onClick={() => remove.mutate(r)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {active.length === 0 && drafts.length === 0 ? (
        <EmptyState
          title={`No ${group === 'inventory' ? 'assets' : 'register entries'} yet`}
          message={canWrite && aiEnabled ? copy.emptyMessage : copy.emptyMessageAiOff}
        />
      ) : (
        active.length > 0 && (
          <Table
            head={
              <>
                <th>Name</th>
                <th>Collection</th>
                <th>Updated</th>
              </>
            }
          >
            {active.map((r) => (
              <tr key={r.id}>
                <td style={{ fontWeight: 600 }}>
                  <Link to={`${copy.base}/${r.collection}`}>{r.name}</Link>
                </td>
                <td>
                  <Badge tone="info">{nameByKey.get(r.collection) ?? r.collection}</Badge>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.updatedAt)}</td>
              </tr>
            ))}
          </Table>
        )
      )}

      {editing && getCollection(editing.collection) && (
        <RecordWizard
          def={getCollection(editing.collection)!}
          record={editing}
          open={!!editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
